import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createColumnHelper, SortingState } from '@tanstack/react-table';
import GenericTable from '@/components/GenericTable/GenericTable';
import LobbyStatusLoader from './LobbyStatusLoader';
import { Friend } from '@/services/friends/friendsServicesTypes';
import { IoEllipsisVerticalSharp } from 'react-icons/io5';
import ActionMenu from '@/pages/friends/components/ActionMenu';
import { useDeleteFriend } from '@/services/friends/queries/useDeleteFriend';
import useShowToast from '@/hooks/useShowToast';
import PlayerNotesModal from '@/features/player-notes/PlayerNotesModal';
import FriendsTableAvatar from '@/components/Friends/FriendsTableAvatar';
import FriendTablesStatus from '@/pages/friends/components/FriendTablesStatus';
import Button, { ButtonSizes } from '@/components/new-design/Button';
import { replacePathParams } from '@/common/utils/utils';
import ROUTES from '@/routers/Routes';
import useIsMobileView from '@/hooks/useIsMobileView';

const columnHelper = createColumnHelper<Friend>();

interface FriendsTableProps {
  data: Friend[];
  isRefetching: boolean;
  isFetching: boolean;
  onClickTransfer: (friendId: string) => void;
  onClickShowTables: (friendId: string) => void;
  refetch: () => void;
}

const FriendsTable: FC<FriendsTableProps> = ({
  onClickShowTables,
  data,
  isRefetching,
  isFetching,
  onClickTransfer,
  refetch,
}) => {
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [clickedUserId, setClickedUserId] = useState('');
  const [actionMenuId, setActionMenuId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<{
    top: number;
    left: number;
  }>({ top: 0, left: 0 });

  const { mutate: mutateDeleteFriend, isSuccess: isDeleteSuccess, isError: isErrorDelete } = useDeleteFriend();
  const isMobile = useIsMobileView();

  useShowToast({
    isSuccess: isDeleteSuccess,
    isError: isErrorDelete,
    successMessage: 'Friend deleted successfully',
    errorMessage: 'Error deleting a friend',
    callbackSuccess: refetch,
  });

  const handleIconClick = (event: React.MouseEvent, friendId: string) => {
    event.stopPropagation();
    const rect = (event.target as HTMLElement).getBoundingClientRect();
    const yValue = isMobile ? 320 : 200;
    const xValue = isMobile ? 200 : 220;
    setMenuPosition({
      top: rect.top + rect.height - yValue,
      left: rect.left - xValue,
    });
    setActionMenuId(friendId);
  };

  const handleCloseMenu = () => {
    setActionMenuId(null);
  };

  const selectFriend = (id: string) => {
    setClickedUserId(id);
    setShowNotesModal(true);
  };

  const [sorting, setSorting] = useState<SortingState>([
    {
      id: 'alias',
      desc: false,
    },
  ]);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const columns = useMemo(
    () => [
      columnHelper.accessor('alias', {
        header: 'Alias',
        enableSorting: true,
        cell: ({ row }) => (
          <FriendsTableAvatar onClick={selectFriend} id={row?.original?.id} alias={row?.original?.alias} />
        ),
      }),
      columnHelper.accessor('seats', {
        header: 'Status',
        enableSorting: true,
        cell: ({ row }) => {
          const onClickShowDetails = () => onClickShowTables(row.original.id);
          return <FriendTablesStatus tables={row.original.seats || []} onClickShowDetails={onClickShowDetails} />;
        },
      }),
      columnHelper.accessor('name', {
        header: 'Name',
        enableSorting: true,
        cell: ({ row }) => (
          <span className="w-[90%] overflow-hidden text-ellipsis whitespace-nowrap text-white text-opacity-50">
            {row.original.firstName} {row.original.lastName}
          </span>
        ),
      }),
      columnHelper.display({
        id: 'actions',
        header: 'Actions',
        cell: ({ row }) => {
          const isOffline = row.original.seats?.length === 0;
          const moreThanOneTable = row.original.seats?.length && row.original.seats?.length > 1;
          const buttonLabel = moreThanOneTable ? 'Show tables' : 'Join Table';
          const onClickShowTablesInternal = () => {
            if (moreThanOneTable) {
              onClickShowTables(row.original.id);
            } else {
              window.open(
                replacePathParams(ROUTES.TABLE, {
                  tableId: (row.original.seats && row.original.seats[0].table.id) || '',
                }),
              );
            }
          };

          return (
            <div className="flex justify-end gap-4">
              <Button
                size={ButtonSizes.md}
                onClick={onClickShowTablesInternal}
                disabled={isOffline}
                className="w-24 md:w-full"
              >
                {buttonLabel}
              </Button>
              <IoEllipsisVerticalSharp
                onClick={handleActionsMenuClickCreator(row.original.id)}
                className="h-10 w-6 cursor-pointer"
              />
            </div>
          );
        },
      }),
    ],
    [],
  );

  const flatData = useMemo(() => data ?? [], [data]);

  const fetchMoreOnBottomReached = React.useCallback(() => {
    // No pagination, so nothing to fetch
  }, []);

  useEffect(() => {
    const container = tableContainerRef.current;
    if (container) {
      const handleScroll = () => {
        fetchMoreOnBottomReached();
      };

      container.addEventListener('scroll', handleScroll);
      return () => container.removeEventListener('scroll', handleScroll);
    }
  }, [fetchMoreOnBottomReached]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!actionMenuRef.current?.contains(event.target as Node)) {
        handleCloseMenu();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const actionMenuRef = useRef<HTMLDivElement>(null);

  const handleActionsMenuClickCreator = (id: string) => (event: React.MouseEvent) => handleIconClick(event, id);

  const handleCloseModal = () => {
    setShowNotesModal(false);
    setClickedUserId('');
  };

  const handleOnClickTransferFounds = useCallback(() => {
    handleCloseMenu();
    if (actionMenuId) onClickTransfer(actionMenuId);
  }, [actionMenuId, onClickTransfer]);

  const handleOnClickRemoveFriend = useCallback(() => {
    if (actionMenuId) mutateDeleteFriend({ id: actionMenuId });
    handleCloseMenu();
  }, [actionMenuId, mutateDeleteFriend]);

  return (
    <div
      className="scrollbar-phenom relative h-full max-h-[70vh] w-full scrollbar-track-transparent scrollbar-thumb-ph-primary-violet"
      ref={tableContainerRef}
    >
      {flatData.length > 0 && (
        <GenericTable
          hover={false}
          onRowClick={undefined}
          data={flatData}
          columns={columns}
          containerRef={tableContainerRef}
          onScrollBottom={fetchMoreOnBottomReached}
          isFetching={isFetching}
          sorting={sorting}
          widthClass="w-[111%] md:w-full"
          onSortingChange={setSorting}
          gridColClass="grid-cols-[1fr_1fr_1fr_auto]"
          gridColBorderClass={
            '[&:nth-child(1)]:border-l [&:nth-child(1)]:border-b [&:nth-child(1)]:border-[rgba(255,255,255,0.15)] [&:nth-child(2)]:border-b [&:nth-child(2)]:border-[rgba(255,255,255,0.15)] [&:nth-child(3)]:border-b [&:nth-child(3)]:border-[rgba(255,255,255,0.15)] [&:nth-child(4)]:border-b [&:nth-child(4)]:border-r [&:nth-child(4)]:border-[rgba(255,255,255,0.15)]'
          }
        />
      )}
      <LobbyStatusLoader isFetching={!isRefetching && isFetching} totalRowCount={flatData?.length || 0} />
      {actionMenuId && (
        <div
          ref={actionMenuRef}
          className="absolute z-50"
          style={{
            top: menuPosition.top,
            left: menuPosition.left,
          }}
        >
          <ActionMenu
            onClickTransferFounds={handleOnClickTransferFounds}
            onClickRemoveFriend={handleOnClickRemoveFriend}
          />
        </div>
      )}
      {showNotesModal && <PlayerNotesModal setClose={handleCloseModal} open={showNotesModal} userId={clickedUserId} />}
    </div>
  );
};

export default FriendsTable;
